/**
 * QL-502 — the builder-side twin of the server's purchasability cap
 * (`apps/api/src/services/goal/purchasability.ts` `isPurchasable` +
 * `goal-portfolio-service.ts` `assertWithinFrozenCap`). A held product that the
 * products-suitability payload no longer lists (inactive, outside the flow's
 * collection, off the goal's platform, or foreign — the payload applies exactly
 * the conditions `isPurchasable` checks) is locked on EVERY flow type. The cap
 * is the frozen `existing_portfolio_snapshot` baseline when one exists for this
 * product, and 0 everywhere else: agreement follow-ups surface their
 * savings-plan lines through the same existing-snapshot read, but the server
 * does not treat them as a frozen baseline (`getFrozenSnapshotTotals` reads
 * `existing_portfolio_snapshot` only), so their cap is 0 too. Review #2 —
 * "frozen baseline" is keyed off the existing-snapshot read's `source`
 * (`existing_portfolio_snapshot` vs the savings-plan fallback), not off the
 * portfolio's *current* `sourceAccountId`: a public-API account deletion nulls
 * `sourceAccountId` but leaves the snapshot rows (and the server's cap) in
 * place, so the UI must not key off the account link either.
 *
 * UX mirror only — the server enforces the rule on every surface.
 */
export type LockedHoldingCap =
  | { locked: false }
  | {
      locked: true;
      /** Highest target amount the server accepts (0 = nothing held: remove only). */
      capAmount: number;
      /** Highest target unit count for units-priced products (null = no unit baseline). */
      capUnits: number | null;
      /**
       * True when a frozen existing-portfolio-snapshot baseline exists (keep /
       * reduce allowed). `false` means the only sanctioned action is removing
       * the row — callers read `!frozen` for that, there is no second flag.
       */
      frozen: boolean;
    };

export function lockedHoldingCap(input: {
  /**
   * Whether the product is listed in the goal's products-suitability
   * payload, or `null` when that isn't known yet (the query hasn't
   * succeeded — still loading, disabled, or errored). `null` never locks:
   * the server stays authoritative, and the builder would rather show a
   * product as purchasable-looking for a moment than wrongly lock, and
   * steer removal of, every holding on a transient fetch failure.
   */
  purchasable: boolean | null;
  /** A frozen `existing_portfolio_snapshot` baseline exists for this product
   * (the existing-snapshot read's `source` was `existing_portfolio_snapshot`,
   * not the savings-plan fallback) — see review #2 above. */
  hasFrozenBaseline: boolean;
  /** Frozen existing amount from the existing-snapshot read (when `hasFrozenBaseline`). */
  existingAmount: number | null;
  /** Frozen existing units from the existing-snapshot read (units products). */
  existingUnits: number | null;
}): LockedHoldingCap {
  if (input.purchasable !== false) return { locked: false };
  const frozen = input.hasFrozenBaseline && input.existingAmount != null;
  return {
    locked: true,
    capAmount: frozen ? (input.existingAmount as number) : 0,
    capUnits: frozen ? input.existingUnits : null,
    frozen,
  };
}

/**
 * QL-502 — per-product frozen totals from the existing-snapshot positions,
 * summed the way the server's `getFrozenSnapshotTotals` sums them: legacy
 * (pre-QL-283) freezes may carry several lots of one instrument, amounts are
 * added in integer cents, and units are the sum of the non-null lot units
 * (no entry when no lot carries units). Positions without a product
 * (unmatchable holdings) are skipped.
 */
export function frozenTotalsByProduct(
  positions: ReadonlyArray<{ productId: string | null; amount: number; units: number | null }>,
): { amounts: Map<string, number>; units: Map<string, number> } {
  const cents = new Map<string, number>();
  const units = new Map<string, number>();
  for (const pos of positions) {
    if (!pos.productId) continue;
    cents.set(pos.productId, (cents.get(pos.productId) ?? 0) + Math.round(pos.amount * 100));
    if (pos.units != null) units.set(pos.productId, (units.get(pos.productId) ?? 0) + pos.units);
  }
  const amounts = new Map<string, number>();
  for (const [productId, c] of cents) amounts.set(productId, c / 100);
  return { amounts, units };
}
